import React, { useState } from 'react';
import { Button, FormControl } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createBranch, deleteBranch, updateBranch } from '../../../api/protocol';
import { HandleApiError } from '../../../components/Notifications/APIErrorHandler';
import { Success } from '../../../components/Notifications/Notifications';
import { useBranches } from './useBranches';
import { Edit, Remove } from '../../../components/Icons/Icons';
import { Tooltip } from '../../../components/Utils/Tooltips';
import { ModalConfirm } from '../../../components/Modals/ModalConfirm';
import Card from '../../../components/Cards/Card';

export const BranchManager = ({ protocolId, disabled, onBranchChanged }) => {
    const { t } = useTranslation();
    const [branches, , setReload] = useBranches(protocolId);
    const [newName, setNewName] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modal, setModal] = useState(null);

    const handleCreate = async () => {
        if (!newName.trim()) return;
        setIsSubmitting(true);
        try {
            await createBranch(protocolId, { name: newName.trim() });
            Success('protocolNotifications.branch_Created');
            setNewName('');
            setReload(true);
            if (onBranchChanged) onBranchChanged();
        } catch (error) {
            HandleApiError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdate = async (branchId) => {
        if (!editingName.trim()) return;
        setIsSubmitting(true);
        try {
            await updateBranch(protocolId, branchId, { name: editingName.trim() });
            Success('protocolNotifications.branch_Updated');
            setEditingId(null);
            setEditingName('');
            setReload(true);
            if (onBranchChanged) onBranchChanged();
        } catch (error) {
            HandleApiError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (branchId) => {
        setIsSubmitting(true);
        try {
            await deleteBranch(protocolId, branchId);
            Success('protocolNotifications.branch_Deleted');
            setReload(true);
            if (onBranchChanged) onBranchChanged();
        } catch (error) {
            HandleApiError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteBranch = (branch) => {
        setModal(
            <ModalConfirm
                title={`${t('protocols.branches.removeBranch')}: ${branch.name}`}
                description={t('protocols.branches.removeBranchDescription')}
                onCancel={() => setModal(null)}
                onConfirm={() => {
                    setModal(null);
                    handleDelete(branch.id);
                }}
            />
        );
    };

    return (
        <Card
            tabs
            header={
                <div className="branch-manager">
                    {!disabled && (
                        <div className="inline-action-group branch-manager__create-row">
                            <FormControl
                                className="inline-action-group__field branch-manager__create-input"
                                type="text"
                                placeholder={t('protocols.branches.newBranchPlaceholder')}
                                value={newName}
                                onChange={e => setNewName(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleCreate()}
                            />
                            <div className="inline-action-group__action">
                                <Button className="branch-manager__create-button" bsStyle="primary" disabled={isSubmitting || !newName.trim()} onClick={handleCreate}>
                                    {t('protocols.branches.addBranch')}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            }
            content={
                <div className="branch-manager">
                    {modal}
                    <div className="table-responsive">
                        <table className="table table-spaced table-spaced-small">
                            <thead>
                                <tr>
                                    <th>{t('protocols.branches.title')}</th>
                                    {!disabled && <th className="header-protocol-actions">{t('protocols.practitioner.actions')}</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {branches.length === 0 ? (
                                    <tr>
                                        <td colSpan={disabled ? 1 : 2}>{t('commons.noData')}</td>
                                    </tr>
                                ) : (
                                    branches.map(branch => (
                                        <tr key={branch.id}>
                                            <td>
                                                {editingId === branch.id ? (
                                                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                        <FormControl
                                                            style={{ flex: 1 }}
                                                            value={editingName}
                                                            onChange={e => setEditingName(e.target.value)}
                                                            onKeyDown={e => e.key === 'Enter' && handleUpdate(branch.id)}
                                                            autoFocus
                                                        />
                                                        <Button bsStyle="success" disabled={isSubmitting} onClick={() => handleUpdate(branch.id)}>
                                                            {t('buttons.save')}
                                                        </Button>
                                                        <Button disabled={isSubmitting} onClick={() => setEditingId(null)}>
                                                            {t('buttons.cancel')}
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <span>{branch.name}</span>
                                                )}
                                            </td>
                                            {!disabled && (
                                                <td className="row-protocol-actions">
                                                    {editingId !== branch.id && (
                                                        <div>
                                                            <Tooltip tooltip={t('buttons.edit')} id={'tooltipedit-' + branch.id}>
                                                                <button
                                                                    onClick={() => { setEditingId(branch.id); setEditingName(branch.name); }}
                                                                    disabled={isSubmitting}
                                                                    className="btn-table-actions">
                                                                    <Edit />
                                                                </button>
                                                            </Tooltip>
                                                            {' '}
                                                            <Tooltip tooltip={t('buttons.delete')} id={'tooltipremove-' + branch.id}>
                                                                <button
                                                                    disabled={isSubmitting}
                                                                    className="btn-remove btn-table-actions"
                                                                    onClick={() => handleDeleteBranch(branch)}
                                                                >
                                                                    <Remove />
                                                                </button>
                                                            </Tooltip>
                                                        </div>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            }
        />
    );
};
